/**
 * Toolbox -- Right-side vertical toolbar panel system
 *
 * Module-aware panels, resizable width, preferences-persisted sizing.
 * This is the HOST for all right-panel features across modules.
 *
 * Architecture:
 * - Vertical icon strip (always visible, 48px wide)
 * - Expandable content panel (240-800px, resizable, persisted width)
 * - Module-specific tools appear based on current route
 * - Persistent tools appear when their module/context gates allow them
 * - Panel content is rendered via renderPanel callback for extensibility
 */

import * as React from 'react'
import { useAtom } from 'jotai'
import type { LucideIcon } from 'lucide-react'
import {
  ChevronLeft,
  ChevronRight,
  History,
  Tag,
  Trash2,
} from 'lucide-react'
import { cn } from '@/shared/utils/cn'
import { getModuleById } from '@/core/registry'
import {
  toolboxActivePanelAtom,
} from '@/lib/stores/toolboxAtoms'
import { useBridgeState } from '@/core/bridge/useBridgeState'

/**
 * Module id that owns the Trash tool. Trash restores deleted documents via the
 * `oracle.trash.*` endpoints — it is an Oracle feature, not cross-module chrome.
 * It must never appear on the dashboard or in other modules (issue: trash icon
 * leaked globally because of an unconditional bottom button).
 */
const TRASH_OWNER_MODULE = 'oracle'

// ============================================================================
// Types
// ============================================================================

export interface ToolboxTool {
  id: string
  name: string
  icon: LucideIcon
  description: string
  /** Whether this tool is always visible (not module-specific) */
  persistent?: boolean
  /** Whether this tool requires a specific condition to be enabled */
  disabled?: boolean
  /** Primary tool gets special styling (e.g. AI) */
  primary?: boolean
  /**
   * Modules this tool should NOT appear in, by module id. Lets the tool
   * itself declare where it's irrelevant instead of a central allowlist
   * (issue #69). Empty/absent = shown everywhere the other gates permit.
   */
  hiddenIn?: readonly string[]
}

interface ToolboxProps {
  /** Current module name (oracle, forge, prism, echo, etc.) */
  currentModule?: string
  /** Whether a versionable entity is open (document, board) */
  hasVersionControl?: boolean
  /** Whether a document is currently open */
  hasActiveDocument?: boolean
  /** Additional module-specific tools */
  moduleTools?: ToolboxTool[]
  /** Render callback for panel content based on active panel ID */
  renderPanel?: (panelId: string) => React.ReactNode | null
  className?: string
}

// ============================================================================
// Default Tool Definitions
// ============================================================================

/**
 * Does this tool want to appear while `currentModule` is active? A tool is
 * hidden from a module when its own `hiddenIn` list contains that module's
 * id — the tool owns the declaration, not a central allowlist (issue #69).
 * When `currentModule` is undefined (dashboard) the predicate is trivially
 * true — dashboard gating is handled by the caller's `inModule` flag.
 */
function isToolVisibleInModule(
  tool: { hiddenIn?: readonly string[] },
  currentModule: string | undefined,
): boolean {
  if (!currentModule) return true
  return !tool.hiddenIn?.includes(currentModule)
}

function getPersistentTools(
  hasVersionControl: boolean,
  hasActiveDocument: boolean,
  currentModule: string | undefined,
): ToolboxTool[] {
  const inModule = !!currentModule
  const activeModule = currentModule ? getModuleById(currentModule) : undefined
  const tagsEnabled = activeModule?.tagsEnabled === true

  // The Toolbox is MODULE-TOOLS-ONLY — it never shows on the dashboard (the
  // shell only mounts it when a module is active) and carries no global
  // chrome. Bug Report + Help are global actions and now live as ActivityBar
  // overlays (chromeOverlayAtom / ChromeOverlays), not toolbox panels.
  //
  // History and Tags are scoped to the active module. History additionally
  // requires an open document. Tags is opt-in: a module must explicitly set
  // `tagsEnabled: true` in its registration once its tag workflow is wired.
  // Trash is narrower still: it restores Oracle's deleted documents
  // (oracle.trash.* endpoints), so it only appears while Oracle is active.
  const isTrashOwner = currentModule === TRASH_OWNER_MODULE
  const all: ToolboxTool[] = [
    ...(inModule && hasActiveDocument ? [{
      id: 'history',
      name: 'Version History',
      icon: History,
      description: hasVersionControl ? 'View version history' : 'No version history available',
      persistent: true,
      disabled: !hasVersionControl,
    }] : []),
    ...(inModule && tagsEnabled ? [
      {
        id: 'tags',
        name: 'Tags',
        icon: Tag,
        description: 'Manage tags',
        persistent: true,
      },
    ] : []),
    ...(isTrashOwner ? [
      {
        id: 'trash',
        name: 'Trash',
        icon: Trash2,
        description: 'Restore deleted pages',
        persistent: true,
      },
    ] : []),
  ]

  return all.filter((t) => isToolVisibleInModule(t, currentModule))
}

function getModuleToolsFromRegistry(
  moduleName: string,
  hasActiveDocument: boolean,
): ToolboxTool[] {
  const mod = getModuleById(moduleName)
  if (!mod?.toolboxTools) return []

  return mod.toolboxTools
    .filter((t) => !t.requiresActiveDocument || hasActiveDocument)
    .filter((t) => isToolVisibleInModule(t, moduleName))
    .map((t) => ({
      id: t.id,
      name: t.name,
      icon: t.icon,
      description: t.description,
      disabled: t.requiresActiveDocument && !hasActiveDocument,
      hiddenIn: t.hiddenIn,
    }))
}

// ============================================================================
// Panel Header Labels
// ============================================================================

/** Panel labels for persistent tools (module tools derive labels from their registration) */
const persistentPanelLabels: Record<string, { title: string; description: string }> = {
  'history': { title: 'Version History', description: 'Document versions' },
  'tags': { title: 'Tags', description: 'Tag management' },
  'trash': { title: 'Trash Bin', description: 'Restore deleted documents' },
}

// ============================================================================
// Component
// ============================================================================

export function Toolbox({
  currentModule,
  hasVersionControl = false,
  hasActiveDocument = false,
  moduleTools: providedModuleTools,
  renderPanel,
  className,
}: ToolboxProps) {
  const [bridgeExpanded, setBridgeExpanded] = useBridgeState<boolean>('ui', 'toolboxExpanded')
  const isExpanded = bridgeExpanded ?? false
  const [activePanel, setActivePanel] = useAtom(toolboxActivePanelAtom)

  // Module-specific tools only appear when a module is active. On the
  // dashboard (currentModule undefined) the Toolbox is cross-module only.
  const persistentTools = getPersistentTools(hasVersionControl, hasActiveDocument, currentModule)
  const contextualTools = currentModule
    ? providedModuleTools || getModuleToolsFromRegistry(currentModule, hasActiveDocument)
    : []

  // Trash is pinned to the bottom of the strip, so it's rendered separately
  // from the top-of-strip persistent tools. It's still sourced from the gated
  // `persistentTools` list (Oracle-only) — absent here means "don't render".
  const trashTool = persistentTools.find((t) => t.id === 'trash')

  // Build label lookup from all tools (persistent labels + contextual tool names)
  const allTools = [...persistentTools, ...contextualTools]
  const panelLabels = React.useMemo(() => {
    const labels: Record<string, { title: string; description: string }> = { ...persistentPanelLabels }
    for (const tool of allTools) {
      if (!labels[tool.id]) {
        labels[tool.id] = { title: tool.name, description: tool.description }
      }
    }
    return labels
  }, [allTools])

  // Toggle a panel. Single close path for every tool (issue #57):
  // - Clicking the already-active tool while expanded closes the toolbox.
  // - Clicking a different tool switches panels (opens if collapsed).
  // - The icon click handlers below just delegate here; no redundant
  //   setBridgeExpanded(true) calls at the call site, which caused
  //   double-writes racing with bridge echoes.
  const togglePanel = React.useCallback((panelId: string) => {
    if (activePanel === panelId && isExpanded) {
      setBridgeExpanded(false)
      return
    }
    setActivePanel(panelId)
    if (!isExpanded) setBridgeExpanded(true)
  }, [activePanel, isExpanded, setActivePanel, setBridgeExpanded])

  // Render panel content
  const panelContent = React.useMemo(() => {
    if (!isExpanded) return null

    // Empty state — expanded with no active tool. Issue #49: the pane used
    // to be blank, leaving new users with no idea what the Toolbox is for.
    if (!activePanel) {
      return (
        <div
          data-testid="toolbox-empty-state"
          className="flex-1 flex items-center justify-center p-6"
        >
          <div className="max-w-xs text-center space-y-3">
            <h4 className="text-sm font-semibold text-white">This is your Toolbox.</h4>
            <p className="text-xs text-ue-text-secondary leading-relaxed">
              Pick an icon on the left to open a tool. Tools change based on the
              module you&apos;re working in — look here for Tags, Trash, and
              anything module-specific.
            </p>
            <p className="text-xs text-ue-text-muted leading-relaxed">
              You can collapse this panel at any time with the arrow at the top.
            </p>
          </div>
        </div>
      )
    }

    // Use external renderPanel if provided
    if (renderPanel) {
      const rendered = renderPanel(activePanel)
      if (rendered) return rendered
    }

    // Default: show panel label with placeholder
    const label = panelLabels[activePanel]
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-sm text-ue-text-secondary">
            {label?.title || activePanel}
          </p>
          <p className="text-xs text-ue-text-muted mt-1">
            Panel not yet connected
          </p>
        </div>
      </div>
    )
  }, [activePanel, isExpanded, renderPanel, panelLabels])

  return (
    <div className={cn('flex h-full min-w-0', className)}>
      {/* Resize is handled by Slate SSplitter — no React resize handle needed */}

      {/* Vertical toolbar strip — always anchored on the LEFT side of toolbox region */}
      <div
        className="flex-none w-12 basis-12 min-w-12 max-w-12 flex flex-col border-l border-r bg-ue-bg-darker border-ue-border-medium"
        data-testid="global-toolbox"
      >
        {/* Toggle button */}
        <div className="p-2">
          <button
            onClick={() => setBridgeExpanded(!isExpanded)}
            className="w-8 h-8 p-0 flex items-center justify-center hover:bg-white/10 rounded text-ue-text-secondary transition-colors"
            title={isExpanded ? 'Collapse Toolbox' : 'Expand Toolbox'}
          >
            {isExpanded ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        {/* Tool icons */}
        <div className="flex flex-col items-center p-2 space-y-2 flex-1 min-h-0">
          {/* Module tools */}
          {contextualTools.slice(0, 5).map((tool) => {
            const isActive = isExpanded && activePanel === tool.id
            return (
              <button
                key={tool.id}
                data-testid={`toolbox-${tool.id}-btn`}
                onClick={() => {
                  if (!tool.disabled) togglePanel(tool.id)
                }}
                className={cn(
                  'w-8 h-8 p-0 flex items-center justify-center rounded transition-colors',
                  tool.disabled
                    ? 'text-neutral-600 cursor-not-allowed'
                    : isActive
                    ? 'bg-ue-accent-primary/20 text-ue-accent-primary hover:bg-ue-accent-primary/30'
                    : 'text-ue-text-secondary hover:bg-white/10'
                )}
                title={tool.description}
                disabled={tool.disabled}
              >
                <tool.icon size={16} />
              </button>
            )
          })}

          {contextualTools.length > 0 && persistentTools.length > 0 && (
            <div className="w-6 h-px bg-ue-border-medium my-1" />
          )}

          {/* Persistent tools (exclude trash -- pushed to bottom) */}
          {persistentTools.filter(t => t.id !== 'trash').slice(0, 5).map((tool) => {
            const isActive = isExpanded && activePanel === tool.id
            const isPrimary = tool.primary === true
            return (
              <button
                key={tool.id}
                data-testid={`toolbox-${tool.id}-btn`}
                onClick={() => {
                  if (!tool.disabled) togglePanel(tool.id)
                }}
                className={cn(
                  'w-8 h-8 p-0 flex items-center justify-center rounded transition-colors',
                  tool.disabled
                    ? 'text-neutral-600 cursor-not-allowed'
                    : isActive
                    ? isPrimary
                      ? 'bg-orange-600/30 text-orange-400 hover:bg-orange-600/40 ring-1 ring-orange-500/50'
                      : 'bg-ue-accent-primary/20 text-ue-accent-primary hover:bg-ue-accent-primary/30'
                    : isPrimary
                    ? 'text-orange-300 hover:bg-orange-600/20 hover:text-orange-400'
                    : 'text-ue-text-secondary hover:bg-white/10'
                )}
                title={tool.name}
                disabled={tool.disabled}
                style={isPrimary && !tool.disabled ? {
                  boxShadow: isActive ? '0 0 8px rgba(255, 107, 53, 0.3)' : '0 0 4px rgba(255, 107, 53, 0.1)'
                } : undefined}
              >
                <tool.icon size={isPrimary ? 18 : 16} />
              </button>
            )
          })}

          {/* Spacer to push trash to bottom */}
          <div className="flex-1" />

          {/* Trash bin -- pinned at bottom, Oracle-only. Gated via persistentTools
              (currentModule === 'oracle') so it never leaks onto the dashboard
              or other modules. */}
          {trashTool && (
            <button
              data-testid="toolbox-trash-btn"
              onClick={() => togglePanel(trashTool.id)}
              className={cn(
                'w-8 h-8 p-0 flex items-center justify-center rounded transition-colors',
                isExpanded && activePanel === 'trash'
                  ? 'bg-ue-accent-primary/20 text-ue-accent-primary hover:bg-ue-accent-primary/30'
                  : 'text-ue-text-secondary hover:bg-white/10'
              )}
              title={trashTool.description}
            >
              <Trash2 size={16} />
            </button>
          )}

        </div>
      </div>

      {/* Expanded Content Panel — fills remaining space (Slate SSplitter controls total width) */}
      {isExpanded && (
        <div
          data-testid="toolbox-content-panel"
          className="flex flex-col h-full border-r relative bg-ue-bg-darker border-ue-border-medium flex-1 min-w-0 overflow-hidden"
        >
          {/* Header */}
          {activePanel && panelLabels[activePanel] && (
            <div className="border-b border-white/10 p-4">
              <h3 className="font-semibold text-sm text-white">
                {panelLabels[activePanel].title}
              </h3>
              <p className="text-xs mt-1 text-ue-text-secondary">
                {panelLabels[activePanel].description}
              </p>
            </div>
          )}

          {/* Panel content */}
          {panelContent}
        </div>
      )}

    </div>
  )
}
